/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { doc } from "firebase/firestore";
import Swal from "sweetalert2";
import { Check, Database, GripVertical, RefreshCw, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { db } from "../../../firebase";
import { applyStoreDraft } from "../../../redux/store/store";

const DEFAULT_COLUMNS = [
  { id: "timestamp", label: "التاريخ والوقت", field: "timestamp", enabled: true },
  { id: "name", label: "اسم العميل", field: "name", enabled: true },
  { id: "orderId", label: "رقم الطلب", field: "orderId", enabled: true },
  { id: "product", label: "المنتج", field: "product", enabled: true },
  { id: "phone", label: "رقم الهاتف", field: "phone", enabled: true },
  { id: "state", label: "الولاية", field: "state", enabled: true },
  { id: "commune", label: "البلدية", field: "commune", enabled: true },
  { id: "address", label: "العنوان", field: "address", enabled: true },
  { id: "qty", label: "الكمية", field: "qty", enabled: true },
  { id: "note", label: "ملاحظة", field: "note", enabled: true },
  { id: "price", label: "السعر", field: "price", enabled: true },
  { id: "deleveryPrice", label: "سعر التوصيل", field: "deleveryPrice", enabled: true },
  { id: "deleveryStatus", label: "حالة التوصيل", field: "deleveryStatus", enabled: true },
  { id: "total", label: "الإجمالي", field: "total", enabled: true },
  { id: "options", label: "الخيارات", field: "options", enabled: true },
  { id: "deliveryType", label: "نوع التوصيل", field: "deliveryType", enabled: true },
  { id: "sku", label: "SKU", field: "sku", enabled: true },
];

const DEFAULT_CONFIG = {
  googleSheetId: "",
  googleSheetName: "",
  frenchs: false,
  arabics: false,
  numberS: false,
  googleSheetColumns: DEFAULT_COLUMNS,
};

const extractSheetIdFromUrl = (url) => {
  const match = String(url || "").match(/\/d\/([a-zA-Z0-9-_]+)/);
  return match ? match[1] : null;
};

export default function ActivationGoogleSheetAbndn({ close }) {
  const dispatch = useDispatch();
  const store = useSelector((state) => state.StoreReducer);
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(DEFAULT_CONFIG);
  const [draggedItem, setDraggedItem] = useState(null);
  const isRTL = i18n.dir() === "rtl";

  const localizeColumns = (columns) =>
    (columns || []).map((column) => ({
      ...column,
      label: t(`orders.sheetabndnConfig.columnsLabels.${column.id}`, {
        defaultValue: column.label,
      }),
    }));

  const storeConfig = useMemo(() => {
    if (Array.isArray(store?.sheetabndn) && store.sheetabndn.length > 0) {
      return store.sheetabndn[0];
    }
    return DEFAULT_CONFIG;
  }, [store]);

  useEffect(() => {
    const fallbackColumns = localizeColumns(DEFAULT_COLUMNS);
    const sourceColumns =
      storeConfig?.googleSheetColumns?.length > 0
        ? storeConfig.googleSheetColumns
        : fallbackColumns;

    setData({
      ...DEFAULT_CONFIG,
      ...storeConfig,
      googleSheetColumns: localizeColumns(sourceColumns),
    });
  }, [storeConfig, i18n.language]);

  const updateData = (updates) => setData((prev) => ({ ...prev, ...updates }));

  const toggleColumn = (columnId) => {
    const updated = (data.googleSheetColumns || DEFAULT_COLUMNS).map((column) =>
      column.id === columnId ? { ...column, enabled: !column.enabled } : column
    );
    updateData({ googleSheetColumns: updated });
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newColumns = [...(data.googleSheetColumns || DEFAULT_COLUMNS)];
    const draggedItemContent = newColumns[draggedItem];
    newColumns.splice(draggedItem, 1);
    newColumns.splice(index, 0, draggedItemContent);

    setDraggedItem(index);
    updateData({ googleSheetColumns: newColumns });
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const resetToDefault = () => {
    updateData({ googleSheetColumns: localizeColumns(DEFAULT_COLUMNS) });
  };

  const handleSheetIdInput = (value) => {
    const extractedId = extractSheetIdFromUrl(value);
    updateData({ googleSheetId: extractedId || value });
  };

  const showAlert = (icon, title, text) =>
    Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: t("servicePageActivation.googleSheet.alerts.confirm"),
      confirmButtonColor: "rgb(141, 93, 167)",
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!store?.id) {
      await showAlert(
        "error",
        t("orders.sheetabndnConfig.storeMissingTitle"),
        t("orders.sheetabndnConfig.storeMissingText")
      );
      return;
    }

    const isDeactivating = !data.googleSheetId || data.googleSheetId.trim() === "";

    if (!isDeactivating) {
      if (!data.googleSheetName || data.googleSheetName.trim() === "") {
        await showAlert(
          "warning",
          t("orders.sheetabndnConfig.sheetNameRequiredTitle"),
          t("orders.sheetabndnConfig.sheetNameRequiredText")
        );
        return;
      }

      if (!data.frenchs && !data.arabics && !data.numberS) {
        await showAlert(
          "warning",
          t("orders.sheetabndnConfig.languageRequiredTitle"),
          t("orders.sheetabndnConfig.languageRequiredText")
        );
        return;
      }

      const enabledColumns = (data.googleSheetColumns || []).filter((col) => col.enabled);
      if (enabledColumns.length === 0) {
        await showAlert(
          "warning",
          t("orders.sheetabndnConfig.columnsRequiredTitle"),
          t("orders.sheetabndnConfig.columnsRequiredText")
        );
        return;
      }
    }

    setLoading(true);
    try {
      const payload = isDeactivating
        ? []
        : [
            {
              googleSheetId: data.googleSheetId.trim(),
              googleSheetName: data.googleSheetName.trim(),
              frenchs: !!data.frenchs,
              arabics: !!data.arabics,
              numberS: !!data.numberS,
              googleSheetColumns: data.googleSheetColumns || DEFAULT_COLUMNS,
            },
          ];

      const updatedStore = {
        ...store,
        sheetabndn: payload,
      };

      await saveStoreDraft(doc(db, "store", store.id), store, updatedStore);
      dispatch(applyStoreDraft(store, updatedStore));

      await showAlert(
        "success",
        isDeactivating
          ? t("orders.sheetabndnConfig.saveSuccessDeactivate")
          : t("orders.sheetabndnConfig.saveSuccessActivate"),
        ""
      );

      if (typeof close === "function") {
        close();
      }
    } catch (error) {
      await showAlert(
        "error",
        t("orders.sheetabndnConfig.saveErrorTitle"),
        t("orders.sheetabndnConfig.saveErrorText")
      );
      console.error("Error saving sheetabndn config:", error);
    } finally {
      setLoading(false);
    }
  };

  const enabledCount = (data.googleSheetColumns || []).filter((col) => col.enabled).length;

  return (
    <div className="min-h-[400px] h-fit p-6" dir={isRTL ? "rtl" : "ltr"}>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#9e68ba] mb-2">
          {t("orders.sheetabndnConfig.title")}
        </h1>
        <p className="text-gray-600">{t("orders.sheetabndnConfig.description")}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-[#fbf4ff] p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("orders.sheetabndnConfig.sheetIdLabel")}
            </label>
            <input
              type="text"
              value={data.googleSheetId || ""}
              onChange={(e) => handleSheetIdInput(e.target.value)}
              placeholder={t("orders.sheetabndnConfig.sheetIdPlaceholder")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
              style={{ direction: "ltr", textAlign: "left" }}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("orders.sheetabndnConfig.sheetNameLabel")}
            </label>
            <input
              type="text"
              value={data.googleSheetName || ""}
              onChange={(e) => updateData({ googleSheetName: e.target.value })}
              placeholder={t("orders.sheetabndnConfig.sheetNamePlaceholder")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("orders.sheetabndnConfig.languageLabel")}
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.frenchs}
                  onChange={() => updateData({ frenchs: true, arabics: false, numberS: false })}
                />
                {t("orders.sheetabndnConfig.languageFrench")}
              </label>
              <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.arabics}
                  onChange={() => updateData({ frenchs: false, arabics: true, numberS: false })}
                />
                {t("orders.sheetabndnConfig.languageArabic")}
              </label>
              <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.numberS}
                  onChange={() => updateData({ frenchs: false, arabics: false, numberS: true })}
                />
                {t("orders.sheetabndnConfig.languageNumbers")}
              </label>
            </div>
          </div>

          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <Database size={18} className="text-purple-600 ml-2" />
                {t("orders.sheetabndnConfig.columnsTitle")}
              </h4>
              <button
                type="button"
                onClick={resetToDefault}
                className="flex items-center text-xs text-purple-600 hover:text-purple-800"
              >
                <RotateCcw size={14} className="ml-1" />
                {t("orders.sheetabndnConfig.resetColumnsOrder", {
                  defaultValue: "إعادة الترتيب الافتراضي",
                })}
              </button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(data.googleSheetColumns || []).map((column, index) => (
                <div
                  key={column.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center p-3 bg-white rounded-lg border cursor-move transition-all ${
                    draggedItem === index
                      ? "opacity-50 border-purple-400"
                      : "border-gray-200 hover:border-purple-300 hover:shadow-sm"
                  }`}
                >
                  <GripVertical
                    size={18}
                    className="text-gray-400 ml-2 flex-shrink-0"
                  />
                  <span className="flex-grow text-sm text-gray-700">{t(`orders.sheetabndnConfig.columnsLabels.${column.id}`, { defaultValue: column.label })}</span>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-500 ml-3">{index + 1}</span>
                    <input
                      type="checkbox"
                      checked={!!column.enabled}
                      onChange={() => toggleColumn(column.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-200"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {t("orders.sheetabndnConfig.enabledColumnsCount", {
                enabled: enabledCount,
                total: (data.googleSheetColumns || []).length,
              })}
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2.5 bg-[#9b59bf] hover:bg-[#814a9f] text-white font-medium rounded-lg transition-colors flex items-center ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin ml-2" size={18} />
                {t("orders.sheetabndnConfig.savingButton")}
              </>
            ) : (
              <>
                <Check className="ml-2" size={18} />
                {t("orders.sheetabndnConfig.saveButton")}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
